import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const EyeSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/>
  </svg>
);

function PwField({ label, value, onChange, placeholder, autoComplete, show, onToggle }) {
  return (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="form-input"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          style={{ paddingRight: 40 }}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggle}
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', lineHeight: 1,
          }}
        >
          {show ? <EyeOffSvg /> : <EyeSvg />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const { user, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw,   setShowCurrentPw]   = useState(false);
  const [showNewPw,       setShowNewPw]       = useState(false);
  const [showConfirmPw,   setShowConfirmPw]   = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/change-password', { currentPassword, newPassword });
      clearMustChangePassword(res.data.accessToken, res.data.refreshToken, res.data.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Password change failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="chpw-wrap">
      <div className="chpw-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{
            width: 34, height: 34, background: 'var(--violet)', borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, color: '#fff', letterSpacing: '-0.01em', flexShrink: 0,
          }}>
            HF
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)' }}>Helicon Flow</div>
            <div style={{ fontSize: 10, color: 'var(--tx-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Journey Viral</div>
          </div>
        </div>

        <div className="chpw-title">Set your password</div>
        <div className="chpw-sub">
          Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Your account requires a new password before you can continue.
        </div>

        <form onSubmit={handleSubmit}>
          <PwField
            label="Current Password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            placeholder="Your current (temp) password"
            autoComplete="current-password"
            show={showCurrentPw}
            onToggle={() => setShowCurrentPw(p => !p)}
          />
          <PwField
            label="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            show={showNewPw}
            onToggle={() => setShowNewPw(p => !p)}
          />
          <PwField
            label="Confirm New Password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Repeat new password"
            autoComplete="new-password"
            show={showConfirmPw}
            onToggle={() => setShowConfirmPw(p => !p)}
          />

          {error && (
            <div className="form-error" style={{ marginBottom: 16 }}>{error}</div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{ width: '100%', padding: '11px', fontSize: 14 }}
          >
            {loading ? 'Updating…' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
